import React from "react";

// Helper function to get color classes
const getColorClasses = (color: string) => {
  const colorMap: Record<
    string,
    { border: string; bg: string; bgLight: string; text: string; hover: string }
  > = {
    blue: {
      border: "border-blue-500",
      bg: "bg-blue-500",
      bgLight: "bg-blue-100",
      text: "text-blue-600",
      hover: "hover:bg-blue-600",
    },
    green: {
      border: "border-green-500",
      bg: "bg-green-500",
      bgLight: "bg-green-100",
      text: "text-green-600",
      hover: "hover:bg-green-600",
    },
    purple: {
      border: "border-purple-500",
      bg: "bg-purple-500",
      bgLight: "bg-purple-100",
      text: "text-purple-600",
      hover: "hover:bg-purple-600",
    },
    red: {
      border: "border-red-500",
      bg: "bg-red-500",
      bgLight: "bg-red-100",
      text: "text-red-600",
      hover: "hover:bg-red-600",
    },
  };

  return colorMap[color] || colorMap.blue; // Default to blue if color not found
};

const Batches: React.FC = () => {
  // Sample batch data
  const batches = [
    {
      id: 1,
      name: "Class IX ",
      level: "O Level",
      schedule: "Mon, Wed, Fri (4:00 PM - 6:00 PM)",

      focus: "Cambridge Curricullam, Notes, Monthly tests, Mock Exams",
      color: "blue",
    },
    {
      id: 2,
      name: "Class X",
      level: "O Level",
      schedule: "Tue, Thu, Sat (5:00 PM - 7:30 PM)",

      focus:
        "Cambridge Curricullam, Notes, Monthly tests, Mock Exams, Practical",
      color: "green",
    },
    {
      id: 3,
      name: "Class XI",
      level: "AS Level",
      schedule: "Sat, Sun (10:00 AM - 12:30 PM)",

      focus:
        "Cambridge Curricullam, Notes, Monthly tests, Mock Exams, Practical",
      color: "purple",
    },
    {
      id: 4,
      name: "Class XII",
      level: "A-Level",
      schedule: "Mon, Thu (3:00 PM - 5:30 PM)",

      focus:
        "Cambridge Curricullam, Notes, Monthly tests, Mock Exams, Practical",
      color: "red",
    },
  ];

  return (
    <section id="batches" className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Batches & Schedule
          </h2>
          <p className="mt-4 max-w-2xl mx-auto text-xl text-gray-500">
            Join our specialized chemistry batches tailored to your educational
            needs
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
          {batches.map((batch) => (
            <div
              key={batch.id}
              className={`bg-white rounded-lg shadow-md overflow-hidden border-t-4 ${
                getColorClasses(batch.color).border
              } hover:shadow-lg transition duration-300`}
            >
              <div className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      {batch.name}
                    </h3>
                    <p
                      className={`inline-block px-2 py-1 rounded-full text-xs font-semibold text-gray-800 ${
                        getColorClasses(batch.color).bgLight
                      } mt-2`}
                    >
                      {batch.level}
                    </p>
                  </div>
                </div>

                <div className="mt-4 space-y-3">
                  <div className="flex items-start">
                    <svg
                      className="h-5 w-5 text-gray-400 mt-0.5 mr-2"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-gray-600">{batch.schedule}</span>
                  </div>

                  <div className="flex items-start">
                    <svg
                      className="h-5 w-5 text-gray-400 mt-0.5 mr-2"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-gray-600">{batch.focus}</span>
                  </div>
                </div>

                <div className="mt-6">
                  <button
                    className={`w-full ${getColorClasses(batch.color).bg} ${
                      getColorClasses(batch.color).hover
                    } text-white py-2 px-4 rounded-md font-medium transition duration-300`}
                  >
                    Enroll Now
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <a
            href="#contact"
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 transition duration-300"
          >
            View all
            <svg
              className="ml-2 -mr-1 h-5 w-5"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
};

export default Batches;
